import { LANES, LOOPS, FINISH_LOOP, getSectionDistances } from '../config/lanes';
import defaultConfig from '../config/timing.json';
import { classifyLaps, averageStats } from './pauseLaps';

export function deduplicatePassings(passings) {
  const sorted = [...passings].sort((a, b) => new Date(a.rtcTime) - new Date(b.rtcTime));
  return sorted.filter((p, i) => {
    if (i === 0) return true;
    const prev = sorted[i - 1];
    if (p.loopId !== prev.loopId) return true;
    return new Date(p.rtcTime) - new Date(prev.rtcTime) > 5000;
  });
}

export function computeLaps(passings, lane = 'buitenbaan', finishLoop = null, cfg = defaultConfig) {
  if (!passings?.length) return [];

  const loops      = cfg.loops ?? LOOPS;
  const lanes      = cfg.lanes ? Object.fromEntries(cfg.lanes.map(l => [l.id, l])) : LANES;
  const finish     = finishLoop ?? cfg.finishLoop ?? FINISH_LOOP;
  const minLapMs   = (cfg.filter?.minLapSeconds ?? 20)  * 1000;
  const maxLapMs   = (cfg.filter?.maxLapSeconds ?? 900) * 1000;
  const laneDist   = lanes[lane]?.distance ?? lanes.buitenbaan?.distance ?? 412.4532;
  const sectionDs  = getSectionDistances(lane, cfg);

  const sorted = deduplicatePassings(passings);

  let lapStart = null;
  let lapCount = 0;
  let passages = {};
  const laps   = [];

  for (const p of sorted) {
    passages[p.loopId] = p.rtcTime;

    if (p.loopId !== finish) continue;

    if (!lapStart) {
      lapStart  = p.rtcTime;
      lapCount  = 0;
      passages  = { [finish]: p.rtcTime };
      continue;
    }

    const lapMs = new Date(p.rtcTime) - new Date(lapStart);
    if (lapMs < minLapMs || lapMs > maxLapMs) {
      lapStart = p.rtcTime;
      passages = { [finish]: p.rtcTime };
      continue;
    }

    lapCount++;

    const rawSections = sectionDs
      .filter(s => {
        const fromTime = s.fromLoopId === finish ? lapStart : passages[s.fromLoopId];
        return fromTime && passages[s.toLoopId];
      })
      .map(s => {
        const fromTime = s.fromLoopId === finish ? lapStart : passages[s.fromLoopId];
        const ms     = new Date(passages[s.toLoopId]) - new Date(fromTime);
        const kmh    = ms > 0 ? (s.distanceM / (ms / 1000)) * 3.6 : 0;
        return {
          sectionIndex:  s.sectionIndex,
          fromLoopId:    s.fromLoopId,
          toLoopId:      s.toLoopId,
          fromName:      s.fromName,
          toName:        s.toName,
          sectionTimeMs: ms,
          sectionStart:  fromTime,
          sectionEnd:    passages[s.toLoopId],
          distanceM:     s.distanceM,
          speedKmh:      kmh,
        };
      });

    const finishSectionIdx = rawSections.findIndex(s => s.fromLoopId === finish);
    const sections = finishSectionIdx > 0
      ? [...rawSections.slice(finishSectionIdx), ...rawSections.slice(0, finishSectionIdx)]
      : rawSections;

    const allLoopIds     = loops.map(l => l.id);
    const skippedLoopIds = allLoopIds.filter(id => !passages[id]);

    laps.push({
      lapId:           `lap_${lapCount}`,
      lapNumber:       lapCount,
      lapStart,
      lapEnd:          p.rtcTime,
      lapTimeMs:       lapMs,
      distanceM:       laneDist,
      speedKmh:        lapMs > 0 ? (laneDist / (lapMs / 1000)) * 3.6 : 0,
      loopsVisited:    Object.keys(passages).length,
      skippedLoopIds,
      sections,
    });

    lapStart = p.rtcTime;
    passages = { [finish]: p.rtcTime };
  }

  return laps;
}

export function computeStats(laps) {
  if (!laps?.length) return null;
  const classified = laps.every(l => typeof l.isPause === 'boolean') ? laps : classifyLaps(laps);

  const times  = laps.map(l => l.lapTimeMs);
  const speeds = laps.map(l => l.speedKmh);
  const sorted = [...times].sort((a, b) => a - b);
  const n      = sorted.length;
  const median = n % 2 === 0
    ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
    : sorted[Math.floor(n / 2)];

  const bestMs  = Math.min(...times);
  const bestIdx = times.indexOf(bestMs);

  return {
    bestLapMs:    bestMs,
    medianLapMs:  median,
    ...averageStats(classified),
    bestSpeedKmh: Math.max(...speeds),
    bestLapIdx:   bestIdx,
  };
}

// Seizoensgrens: 1 juli – 30 juni
export function getSeasonRange(year) {
  return {
    from: new Date(`${year}-07-01T00:00:00Z`).toISOString(),
    to:   new Date(`${year + 1}-07-01T00:00:00Z`).toISOString(),
    label: `${year}–${String(year + 1).slice(2)}`,
  };
}

export function currentSeasonYear() {
  const now   = new Date();
  const month = now.getMonth() + 1;
  return month >= 7 ? now.getFullYear() : now.getFullYear() - 1;
}

// Kalenderweek (maandag 00:00 UTC t/m volgende maandag) rond `now`. `now` is
// injecteerbaar zodat de functie deterministisch te testen is.
export function getWeekRange(now = new Date()) {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysSinceMonday = (d.getUTCDay() + 6) % 7; // zo=0 → 6, ma=1 → 0
  const monday = new Date(d);
  monday.setUTCDate(d.getUTCDate() - daysSinceMonday);
  const next = new Date(monday);
  next.setUTCDate(monday.getUTCDate() + 7);
  return { from: monday.toISOString(), to: next.toISOString(), label: 'Deze week' };
}

// Kalendermaand (1e 00:00 UTC t/m 1e van de volgende maand) rond `now`.
export function getMonthRange(now = new Date()) {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to   = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { from: from.toISOString(), to: to.toISOString(), label: 'Deze maand' };
}
